const valueOrFallback = value => value ?? "Not provided";

const acceptanceLabel = accepted => accepted === true ? "Yes" : "No";

const formatDate = value => {
    if (!value) return "Not provided";

    const date = new Date(value);
    return Number.isNaN(date.getTime())
        ? "Not provided"
        : date.toLocaleDateString("en-GB", {
            year: "numeric",
            month: "long",
            day: "numeric"
        });
};

const addNarrativeSection = (content, htmlToPdfmake, {
    title,
    comment,
    acceptance
}) => {
    content.push({
        "text": title,
        "fontSize": 12,
        "bold": true,
        "margin": [0, 16, 0, 6]
    });

    content.push({
        "columns": acceptance.map(({ label, accepted }) => ({
            "text": `${label} Accepted: ${acceptanceLabel(accepted)}`,
            "width": "auto",
            "margin": [0, 0, 16, 6]
        }))
    });

    const narrative = htmlToPdfmake(comment || "<p>Not provided</p>", {
        ignoreStyles: ["font-family"]
    });
    content.push(...(Array.isArray(narrative) ? narrative : [narrative]));
};

export const generateNonConformityReport = async ({ pageItem, attachedItems, store }, {
    reportTitle,
    filenamePrefix
}) => {
    const [{ downloadPdf }, { default: htmlToPdfmake }] = await Promise.all([
        import("../../../pdfDownloader.js"),
        import("html-to-pdfmake")
    ]);

    const content = [{
        "text": `${reportTitle}: ${valueOrFallback(pageItem?.Number)}`,
        "fontSize": 16,
        "bold": true,
        "margin": [0, 0, 0, 12]
    }, {
        "table": {
            "widths": ["25%", "25%", "25%", "25%"],
            "body": [[
                { "text": "Number", "bold": true },
                { "text": "Audit Report", "bold": true },
                { "text": "Procedure", "bold": true },
                { "text": "Due Date", "bold": true }
            ], [
                valueOrFallback(pageItem?.Number),
                valueOrFallback(pageItem?.Report),
                valueOrFallback(pageItem?.Procedure),
                formatDate(pageItem?.Date?.Due)
            ], [
                { "text": "Type", "bold": true },
                { "text": "Severity", "bold": true },
                {},
                {}
            ], [
                valueOrFallback(pageItem?.Type),
                valueOrFallback(pageItem?.Severity),
                {},
                {}
            ]]
        },
        "layout": "mainTableConfig"
    }];

    addNarrativeSection(content, htmlToPdfmake, {
        "title": "Issue",
        "comment": pageItem?.Issue?.Comment,
        "acceptance": [{ "label": "HoD", "accepted": pageItem?.Issue?.HOD }]
    });
    addNarrativeSection(content, htmlToPdfmake, {
        "title": "Root Cause",
        "comment": pageItem?.RootCause?.Comment,
        "acceptance": [
            { "label": "HISM", "accepted": pageItem?.RootCause?.DAM },
            { "label": "HoD", "accepted": pageItem?.RootCause?.HOD }
        ]
    });
    addNarrativeSection(content, htmlToPdfmake, {
        "title": "Corrective Action",
        "comment": pageItem?.CorrectiveAction?.Comment,
        "acceptance": [
            { "label": "HISM", "accepted": pageItem?.CorrectiveAction?.DAM },
            { "label": "HoD", "accepted": pageItem?.CorrectiveAction?.HOD }
        ]
    });
    addNarrativeSection(content, htmlToPdfmake, {
        "title": "Preventative Action",
        "comment": pageItem?.PreventativeAction?.Comment,
        "acceptance": [
            { "label": "HISM", "accepted": pageItem?.PreventativeAction?.DAM },
            { "label": "HoD", "accepted": pageItem?.PreventativeAction?.HOD }
        ]
    });

    const jobCards = (attachedItems?.JobCards?.items || [])
        .map(itemId => store.state[itemId])
        .filter(item => item && item.Cancelled !== true);
    const jobCardRows = jobCards.length > 0
        ? jobCards.map(item => [
            valueOrFallback(item.Number),
            valueOrFallback(item.Title),
            valueOrFallback(item.Type)
        ])
        : [[{ "text": "No job cards", "colSpan": 3 }, {}, {}]];

    content.push({
        "text": "Job Cards",
        "fontSize": 12,
        "bold": true,
        "margin": [0, 16, 0, 6]
    }, {
        "table": {
            "headerRows": 1,
            "widths": ["20%", "60%", "20%"],
            "body": [[
                { "text": "Number", "bold": true },
                { "text": "Title", "bold": true },
                { "text": "Colour", "bold": true }
            ], ...jobCardRows]
        },
        "layout": "mainTableConfig"
    });

    downloadPdf({ content }, `${filenamePrefix}_${valueOrFallback(pageItem?.Number)}.pdf`);
};

export const nonConformityReport = context => generateNonConformityReport(context, {
    "reportTitle": "Non Conformity Report",
    "filenamePrefix": "NonConformityReport"
});
